import { fetcher, OlaggNewsEndpoints, fetcherNewsRoom, NewsEndpoints } from "@olagg/api-hooks";
import { NewsType, NewsRanking } from "@olagg/db-types";
import { IUseNews } from "@olagg/api-hooks/interfaces/IUseNews";
import { useContext, useState } from "react";
import { analytics } from "../../../analytics";
import { useReferrals } from '../../Referrals/hooks/useReferrals';
import { AdTrackingContext } from '@/contexts/adTrackingContext';

interface ISuggestions {
  author: string[];
  tag: string[];
  content_type: string[];
  author_type: string[];
}


const useNews = () => {
  const { fcb, fbp } = useContext(AdTrackingContext)
  const [news, setNews] = useState<NewsType[]>([]);
  const [count, setCount] = useState<number>(0);
  const [singleNews, setSingleNews] = useState<NewsType | null>(null);
  const [singleRelatedNews, setSingleRelatedNews] = useState(null);
  const [isLoading, setIsLoading] = useState<boolean>(true)
  const [isLoadingMore, setIsLoadingMore] = useState<boolean>(false)
  const [isLoadingRanking, setIsLoadingRanking] = useState<boolean>(false)
  const { ActionType, validateRelevantAction } = useReferrals();
  const [suggestions, setSuggestions] = useState<ISuggestions>({
    author: [],
    tag: [],
    content_type: [],
    author_type: [],
  })
  const [showNext, setShowNext] = useState<boolean>(true)
  const [ranking, setRanking] = useState<NewsRanking[]>([]);

  const last = (filter: IUseNews) => {
    setIsLoading(true)
    fetcher(OlaggNewsEndpoints.last(filter))
      .then((res) => {
        setNews(res.news)
        setCount(res.meta.count)
      }).finally(() => setIsLoading(false))
  }

  const multiSearch = (filter: IUseNews) => {
    filter = {
      ...filter,
      lang: filter.lang ?? 'es'
    };

    if (filter.limit > 12) {
      setIsLoadingMore(true)
      setIsLoading(false)
    } else {
      setIsLoadingMore(false)
      setIsLoading(true)
    }

    fetcher(OlaggNewsEndpoints.multiSearch(filter))
      .then((res) => {
        const { results, authors, content_types, tags, count, author_type } = res

        setSuggestions({
          author: authors,
          tag: tags,
          content_type: content_types,
          author_type: author_type,
        })

        if (results.length < filter.limit) {
          setShowNext(false)
        } else {
          setShowNext(true)
        }

        setNews(results)
        setCount(count)
        if (filter.limit == 12 && filter.q != '') {
          let props = {
            search_keywords: filter.q,
            results: results.length,
            user_agent: navigator.userAgent
          }
          if (fcb != null) props.fbc = fcb
          if (fbp != null) props.fbp = fbp
          analytics()?.track('News Searched', props);
        }
      }).finally(() => {
        (filter.limit > 12) ? setIsLoadingMore(false) : setIsLoading(false)
      })
  }

  const getBySlug = (slug: string, preview: boolean = false, update: boolean = false, entityName = 'novedades') => {
    setIsLoading(true)
    fetcher(OlaggNewsEndpoints.find(slug, preview, update, entityName))
      .then((res) => {
        setSingleNews(res?.news)
        setSingleRelatedNews(res?.news?.relatedNews)
        if (res?.news?.slug) {
          const { totalUpVotes, totalDownVotes, myVote } = res.news?.votes || { totalUpVotes: 0, totalDownVotes: 0 }
          const analyticsData = {
            news: res.news.slug,
            content_type: "news",
            content_id: res.news?.id,
            author_user_id: res.news?.author?.external_user_id,
            tags: res.news?.tags && Array.from(res.news?.tags, tag => tag.name),
            total_upvotes: Number(totalUpVotes),
            total_downvotes: Number(totalDownVotes),
          };

          if (fcb != null) analyticsData.fbc = fcb
          if (fbp != null) analyticsData.fbp = fbp

          analytics()?.page(
            'News Details',
            analyticsData, {
            user_agent: navigator.userAgent
          })
        }
      }).catch((error) => {
        setSingleNews(null)
        setSingleRelatedNews(null)
        console.log(error)
      }).finally(() => {
        setIsLoading(false)
      })
  }

  const getRelatednewsBySlug = (slug: string) =>
    fetcherNewsRoom(NewsEndpoints.bySlug(slug))
      .then((res) => {
        setSingleRelatedNews(res.relateds)
      })

  const vote = async (value: number, entityName = 'novedades') => {
    const vote = singleNews.votes.myVote === value ? 0 : value;
    return await fetcher(OlaggNewsEndpoints.vote(singleNews.slug, vote, entityName))
      .then((res) => {
        setSingleNews(res.news);
        validateRelevantAction(ActionType.VOTE_NEWS);
      })
  };

  const getRanking = () => {
    setIsLoadingRanking(true)
    fetcher(OlaggNewsEndpoints.ranking())
      .then((res) => {
        setRanking(res.ranking)
      }).finally(() => setIsLoadingRanking(false))
  }

  return {
    news,
    last,
    count,
    singleNews,
    getBySlug,
    // search,
    vote,
    getRelatednewsBySlug,
    singleRelatedNews,
    isLoading,
    setIsLoading,
    isLoadingMore,
    setIsLoadingMore,
    multiSearch,
    suggestions,
    showNext,
    ranking,
    getRanking,
    isLoadingRanking
  };
}

export default useNews;
